import { useEffect, useRef, useCallback, useState, useMemo } from 'react';
import { SpeakerHigh, SpeakerSlash, CornersOut, CornersIn } from '@phosphor-icons/react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import {
  resolveSignalConfig,
  resolveRhythm,
  sampleECG,
  samplePleth,
  sampleRespiration,
  WaveformCanvas,
  FALLBACK_RHYTHM_LABELS,
} from './waveforms';

// Calculate PAM (Presión Arterial Media)
// Formula: PAM = (Sistólica + 2×Diastólica) / 3
export function calculatePAM(sistolica, diastolica) {
  if (!sistolica || !diastolica) return null;
  return Math.round((sistolica + 2 * diastolica) / 3);
}

// Get PAM status color based on value
function getPAMStatus(pam) {
  if (!pam) return { color: 'text-slate-400', status: 'N/A' };
  if (pam < 60) return { color: 'text-red-500', status: 'Crítico bajo' };
  if (pam < 65) return { color: 'text-orange-500', status: 'Bajo' };
  if (pam <= 100) return { color: 'text-green-400', status: 'Normal' };
  if (pam <= 110) return { color: 'text-yellow-500', status: 'Elevada' };
  return { color: 'text-red-500', status: 'Crítico alto' };
}

export const VitalMonitor = ({ vitals, patientState, showPAM = true, scenario }) => {
  const audioContextRef = useRef(null);
  const lastBeepTimeRef = useRef(0);
  const alarmOscillatorRef = useRef(null);
  const [audioEnabled, setAudioEnabled] = useState(false);
  // v10.3.8: modo pantalla completa activable con doble-click. Oculta el
  // resto del layout y expande las 3 ondas a todo el viewport — ideal para
  // simulacros con proyector o simulaciones remotas.
  const monitorRootRef = useRef(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const enterFullscreen = useCallback(async () => {
    const el = monitorRootRef.current;
    if (!el) return;
    try {
      if (el.requestFullscreen) await el.requestFullscreen();
      else if (el.webkitRequestFullscreen) await el.webkitRequestFullscreen();
      else if (el.mozRequestFullScreen) await el.mozRequestFullScreen();
    } catch (_e) {
      // Fallback: aún así activamos overlay CSS full-viewport
    }
    setIsFullscreen(true);
  }, []);
  const exitFullscreen = useCallback(async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else if (document.webkitFullscreenElement) await document.webkitExitFullscreen();
    } catch (_e) { /* ignore */ }
    setIsFullscreen(false);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (isFullscreen) exitFullscreen();
    else enterFullscreen();
  }, [isFullscreen, enterFullscreen, exitFullscreen]);
  // Sincroniza con la API nativa (ESC del navegador, etc.)
  useEffect(() => {
    const onChange = () => {
      const active = !!(document.fullscreenElement || document.webkitFullscreenElement);
      setIsFullscreen(active);
    };
    document.addEventListener('fullscreenchange', onChange);
    document.addEventListener('webkitfullscreenchange', onChange);
    return () => {
      document.removeEventListener('fullscreenchange', onChange);
      document.removeEventListener('webkitfullscreenchange', onChange);
    };
  }, []);

  // v10.3.0: motor fisiológico unificado. Los generadores derivan sus
  // muestras de esta config → ondas coherentes (ECG dispara el QRS →
  // Pleth aparece ~200ms después vía Pulse Transit Delay).
  const cfg = useMemo(() => {
    const c = resolveSignalConfig(vitals || {}, patientState);
    c._vitals = vitals || {};
    return c;
    // Recalcular sólo cuando cambien parámetros relevantes al motor.
    // No listamos `vitals` completo porque su identidad puede cambiar
    // cada render sin que cambien los campos que nos importan.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    vitals?.fc, vitals?.ritmo_ecg, vitals?.ecg_amplitude, vitals?.pleth_amplitude,
    vitals?.respiratory_amplitude, vitals?.spo2, vitals?.pa_sistolica, vitals?.pa_diastolica,
    vitals?.fr, vitals?.patron_respiratorio, vitals?.perfusion, vitals?.pulse_rate,
    vitals?.signal_quality, vitals?.noise_level, vitals?.artifact_type,
    patientState,
  ]);

  // Samplers estables entre renders (referencia a `cfg` es fresca por closure)
  const ecgSampler = useCallback((t) => sampleECG(t, cfg), [cfg]);
  const plethSampler = useCallback((t) => samplePleth(t, cfg), [cfg]);
  const respSampler = useCallback((t) => sampleRespiration(t, cfg), [cfg]);

  // Create audio context
  const initAudio = useCallback(() => {
    if (!audioContextRef.current) {
      try {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
        setAudioEnabled(true);
      } catch (e) {
        console.error('Failed to create AudioContext:', e);
      }
    } else if (audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume();
      setAudioEnabled(true);
    }
  }, []);

  // Play heartbeat beep (single beat)
  const playHeartbeatBeep = useCallback((frequency = 800, duration = 80) => {
    if (!audioContextRef.current || audioContextRef.current.state !== 'running') return;

    const now = Date.now();
    if (now - lastBeepTimeRef.current < 150) return; // Debounce
    lastBeepTimeRef.current = now;

    try {
      const ctx = audioContextRef.current;
      const currentTime = ctx.currentTime;
      
      // Create oscillator for the beep
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      osc.frequency.value = frequency;
      osc.type = 'sine';
      
      // Quick attack, short decay
      gainNode.gain.setValueAtTime(0, currentTime);
      gainNode.gain.linearRampToValueAtTime(0.15, currentTime + 0.01);
      gainNode.gain.exponentialRampToValueAtTime(0.001, currentTime + duration / 1000);
      
      osc.start(currentTime);
      osc.stop(currentTime + duration / 1000);
    } catch (e) {
      console.error('Beep error:', e);
    }
  }, []);

  // Play alarm sound (continuous, alternating frequencies)
  const startAlarm = useCallback(() => {
    if (!audioContextRef.current || alarmOscillatorRef.current) return;

    try {
      const ctx = audioContextRef.current;
      const osc = ctx.createOscillator();
      const gainNode = ctx.createGain();
      
      osc.connect(gainNode);
      gainNode.connect(ctx.destination);
      
      osc.type = 'square';
      gainNode.gain.value = 0.1;
      
      // Alternating alarm pattern
      const alarmPattern = () => {
        if (!alarmOscillatorRef.current) return;
        const t = ctx.currentTime;
        osc.frequency.setValueAtTime(880, t);
        osc.frequency.setValueAtTime(660, t + 0.25);
        osc.frequency.setValueAtTime(880, t + 0.5);
        osc.frequency.setValueAtTime(660, t + 0.75);
      };
      
      alarmPattern();
      const alarmInterval = setInterval(alarmPattern, 1000);
      
      osc.start();
      alarmOscillatorRef.current = { osc, gainNode, interval: alarmInterval };
    } catch (e) {
      console.error('Alarm error:', e);
    }
  }, []);

  const stopAlarm = useCallback(() => {
    if (alarmOscillatorRef.current) {
      try {
        clearInterval(alarmOscillatorRef.current.interval);
        alarmOscillatorRef.current.osc.stop();
        alarmOscillatorRef.current = null;
      } catch (e) {
        console.error('Stop alarm error:', e);
      }
    }
  }, []);

  // Toggle audio on click
  const toggleAudio = () => {
    if (!audioEnabled) {
      initAudio();
    } else {
      if (audioContextRef.current) {
        audioContextRef.current.suspend();
        stopAlarm();
      }
      setAudioEnabled(false);
    }
  };

  // v10.3.9: MonitorAudioEngine coherente con `cfg` (resolveSignalConfig).
  // Un ÚNICO scheduler recursivo con setTimeout que re-lee cfg en cada
  // beep — así el intervalo se adapta inmediatamente cuando cambia FC /
  // ritmo / perfusión / SpO₂. Skipeamos beeps para ritmos sin pulso
  // efectivo (asistolia, FV, AESP) o cuando perfusion === 0.
  // También el pitch varía con SpO₂ (98 → 880Hz, 70 → 500Hz).
  const cfgRef = useRef(cfg);
  cfgRef.current = cfg;
  useEffect(() => {
    if (!audioEnabled || !audioContextRef.current || audioContextRef.current.state !== 'running') return undefined;

    let timeoutId = null;
    let cancelled = false;

    const scheduleNextBeep = () => {
      if (cancelled) return;
      const c = cfgRef.current || {};
      const rhythm = c.rhythm;
      const hr = c.pulseRate || c.hr || 0;
      const perfusion = c.perfusion ?? 1;
      // Sin pulso efectivo → no reagendar (silencio hasta que cambie estado)
      const pulseless = ['asistolia', 'fibrilacion_ventricular', 'aesp'].includes(rhythm);
      if (pulseless || hr <= 0 || perfusion <= 0.05) {
        // Verificar periódicamente por si el estado cambia y vuelve a haber pulso
        timeoutId = setTimeout(scheduleNextBeep, 500);
        return;
      }
      // Pitch mapea SpO₂: 100→880Hz, 85→720Hz, 70→560Hz
      const spo2 = typeof c.spo2 === 'number' ? c.spo2 : 98;
      const clampedSpo2 = Math.max(60, Math.min(100, spo2));
      const beepFrequency = Math.round(500 + (clampedSpo2 - 60) * (880 - 500) / 40);
      playHeartbeatBeep(beepFrequency, 60);
      const interval = 60000 / Math.max(20, Math.min(220, hr));
      timeoutId = setTimeout(scheduleNextBeep, interval);
    };

    scheduleNextBeep();

    return () => {
      cancelled = true;
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [audioEnabled, playHeartbeatBeep]);

  // v10.3.9: alarma para estados críticos — asistolia, FV, AESP,
  // paro clínico, SpO₂ < 85. Distinta del tono normal de pulso.
  useEffect(() => {
    if (!audioEnabled || !audioContextRef.current) return undefined;

    const c = cfg || {};
    const critical =
      patientState === 'paro' ||
      patientState === 'critico' ||
      ['asistolia', 'fibrilacion_ventricular', 'aesp'].includes(c.rhythm) ||
      (typeof c.spo2 === 'number' && c.spo2 < 85);

    if (critical) startAlarm();
    else stopAlarm();

    return () => stopAlarm();
  }, [cfg, patientState, audioEnabled, startAlarm, stopAlarm]);


      // Waveform animation — v10.3.0: ahora manejado por el motor
  // PhysiologicalWaveformEngine + WaveformCanvas directamente en cada
  // canvas. No usamos setInterval + setState porque impedía 60fps y
  // desacoplaba ondas independientes (ECG/Pleth). El motor comparte una
  // sola configuración (cfg) → coherencia garantizada.

  const getVitalColor = (type) => {
    switch (type) {
      case 'fc': return '#22C55E';
      case 'spo2': return '#06B6D4';
      case 'pan': return '#EF4444';
      case 'pam': return '#F97316';
      case 'fr': return '#EAB308';
      case 'temp': return '#A855F7';
      default: return '#ffffff';
    }
  };

  // Calculate PAM value
  const pamValue = useMemo(() => {
    return calculatePAM(vitals.pa_sistolica, vitals.pa_diastolica);
  }, [vitals.pa_sistolica, vitals.pa_diastolica]);

  // v10.3.8: altura dinámica de las ondas en fullscreen.
  // Track viewport height para que cuando el usuario rota o cambia
  // tamaño de ventana, las ondas se ajusten sin recargar.
  const [viewportH, setViewportH] = useState(
    typeof window !== 'undefined' ? window.innerHeight : 900
  );
  useEffect(() => {
    const onResize = () => setViewportH(window.innerHeight);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  const waveHeight = isFullscreen
    ? Math.max(160, Math.floor((viewportH - 260) / 3))
    : 130;
  const scaleFactor = waveHeight / 130;
  const ecgScale = 1.5 * scaleFactor;
  const plethScale = 1.8 * scaleFactor;
  const respScale = 2.4 * scaleFactor;

  const pamStatus = useMemo(() => getPAMStatus(pamValue), [pamValue]);

  // Determine if scenario requires PAM display
  const shouldShowPAM = showPAM || scenario?.requires_pam || (pamValue && pamValue < 65);

  const getAlarmClass = () => {
    if (patientState === 'paro') return 'alarm-critical bg-red-900/30';
    if (patientState === 'critico') return 'alarm-critical';
    return '';
  };

  return (
    <div
      ref={monitorRootRef}
      className={`${
        isFullscreen
          ? 'fixed inset-0 z-[100] bg-black rounded-none border-0'
          : 'h-full bg-black rounded-lg border border-slate-700'
      } overflow-hidden ${getAlarmClass()}`}
      data-testid="vital-monitor"
      data-fullscreen={isFullscreen ? 'true' : 'false'}
      onDoubleClick={toggleFullscreen}
      title={isFullscreen ? 'Doble click o ESC para salir' : 'Doble click para pantalla completa'}
    >
      {/* Fullscreen toggle */}
      <button
        onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
        className="absolute top-2 right-14 z-10 p-2 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
        data-testid="vital-monitor-fullscreen-toggle"
        title={isFullscreen ? 'Salir de pantalla completa' : 'Pantalla completa'}
      >
        {isFullscreen ? (
          <CornersIn size={20} className="text-slate-300" />
        ) : (
          <CornersOut size={20} className="text-slate-300" />
        )}
      </button>

      {/* Audio Toggle Button */}
      <button
        onClick={(e) => { e.stopPropagation(); toggleAudio(); }}
        className="absolute top-2 right-2 z-10 p-2 rounded-full bg-slate-800 hover:bg-slate-700 transition-colors"
        data-testid="audio-toggle"
        title={audioEnabled ? 'Silenciar' : 'Activar sonido'}
      >
        {audioEnabled ? (
          <SpeakerHigh size={20} className="text-green-400" />
        ) : (
          <SpeakerSlash size={20} className="text-slate-400" />
        )}
      </button>

      {/* Hint discreto de doble-click cuando NO estás en fullscreen */}
      {!isFullscreen && (
        <div
          className="absolute bottom-1 right-2 text-[10px] text-slate-500/50 pointer-events-none select-none"
          data-testid="vital-monitor-fullscreen-hint"
        >
          Doble click para pantalla completa
        </div>
      )}

      {/* Monitor Grid */}
      <div className="h-full grid grid-rows-[auto_auto] gap-0">
        {/* Top Section - 3 Waveforms (ECG, Pleth, Resp) */}
        <div className="grid grid-cols-1 md:grid-cols-3 border-b border-slate-800">
          {/* ECG Waveform */}
          <div className="border-r border-slate-800 monitor-grid relative">
            <WaveformCanvas
              sampler={ecgSampler}
              height={waveHeight}
              color="#22c55e"
              yScale={ecgScale}
              pxPerSecond={90}
              label="ECG II"
              sublabel={FALLBACK_RHYTHM_LABELS[vitals.ritmo_ecg || resolveRhythm(vitals, patientState)] || (vitals.ritmo_ecg || '')}
              numericValue={vitals.fc ?? '--'}
              numericUnit="lpm"
              numericColor="#22c55e"
              testId="vital-monitor-ecg-canvas"
            />
          </div>

          {/* Pleth Waveform */}
          <div className="border-r border-slate-800 monitor-grid relative">
            <WaveformCanvas
              sampler={plethSampler}
              height={waveHeight}
              color="#06b6d4"
              yScale={plethScale}
              pxPerSecond={90}
              label="Pleth"
              numericValue={vitals.spo2 != null ? `${vitals.spo2}` : '--'}
              numericUnit="%"
              numericColor="#06b6d4"
              testId="vital-monitor-pleth-canvas"
            />
          </div>

          {/* Respiratory Waveform (v10.3.0 nuevo) */}
          <div className="monitor-grid relative">
            <WaveformCanvas
              sampler={respSampler}
              height={waveHeight}
              color="#eab308"
              yScale={respScale}
              pxPerSecond={45}
              label="Resp"
              numericValue={vitals.fr ?? '--'}
              numericUnit="rpm"
              numericColor="#eab308"
              testId="vital-monitor-resp-canvas"
            />
          </div>
        </div>

        {/* Bottom Section - Digital Displays */}
        <div className="grid grid-cols-3 md:grid-cols-6 gap-0">
          {/* FC */}
          <div className="border-r border-slate-800 p-3 flex flex-col justify-center items-center">
            <span className="text-xs text-slate-400 mb-1">FC</span>
            <span className="font-vital text-3xl md:text-4xl vital-fc" data-testid="vital-fc-value">
              {vitals.fc || '--'}
            </span>
            <span className="text-xs text-slate-500">lpm</span>
          </div>

          {/* SpO2 */}
          <div className="border-r border-slate-800 p-3 flex flex-col justify-center items-center">
            <span className="text-xs text-slate-400 mb-1">SpO2</span>
            <span className="font-vital text-3xl md:text-4xl vital-spo2" data-testid="vital-spo2-value">
              {vitals.spo2 || '--'}
            </span>
            <span className="text-xs text-slate-500">%</span>
          </div>

          {/* PAN (Presión Arterial No Invasiva) */}
          <div className="border-r border-slate-800 p-3 flex flex-col justify-center items-center">
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="text-xs text-slate-400 mb-1 cursor-help border-b border-dotted border-slate-500">PAN</span>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                  <p className="text-xs">Presión Arterial No Invasiva</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            <span className="font-vital text-2xl md:text-3xl vital-pa" data-testid="vital-pan-value">
              {vitals.pa_sistolica || '--'}/{vitals.pa_diastolica || '--'}
            </span>
            <span className="text-xs text-slate-500">mmHg</span>
          </div>

          {/* PAM (Presión Arterial Media) - Calculated */}
          {shouldShowPAM && (
            <div className="border-r border-slate-800 p-3 flex flex-col justify-center items-center">
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <span className="text-xs text-slate-400 mb-1 cursor-help border-b border-dotted border-slate-500">PAM</span>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-slate-800 border-slate-700">
                    <p className="text-xs">Presión Arterial Media</p>
                    <p className="text-[10px] text-slate-400">= (Sis + 2×Dia) / 3</p>
                    <p className={`text-[10px] ${pamStatus.color}`}>{pamStatus.status}</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
              <span className={`font-vital text-2xl md:text-3xl ${pamStatus.color}`} data-testid="vital-pam-value">
                {pamValue || '--'}
              </span>
              <span className="text-xs text-slate-500">mmHg</span>
            </div>
          )}

          {/* FR */}
          <div className="border-r border-slate-800 p-3 flex flex-col justify-center items-center hidden md:flex">
            <span className="text-xs text-slate-400 mb-1">FR</span>
            <span className="font-vital text-3xl md:text-4xl vital-fr" data-testid="vital-fr-value">
              {vitals.fr || '--'}
            </span>
            <span className="text-xs text-slate-500">rpm</span>
          </div>

          {/* Temperature */}
          <div className="p-3 flex flex-col justify-center items-center hidden md:flex">
            <span className="text-xs text-slate-400 mb-1">Temp</span>
            <span className="font-vital text-3xl md:text-4xl vital-temp" data-testid="vital-temp-value">
              {vitals.temperatura?.toFixed(1) || '--'}
            </span>
            <span className="text-xs text-slate-500">°C</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VitalMonitor;
